import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
    BulkImportCandidate,
    BulkImportResponse,
    bulkImportEasyflexWorkers,
    EasyFlexWorker,
    EasyFlexWorkerFilters,
    fetchBulkImportCandidates,
} from '../api/workersApi';

const DEFAULT_LIMIT = 25;

export default function BulkImportPage() {
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [profession, setProfession] = useState<string>('');
    const [hasActivePlacement, setHasActivePlacement] = useState<string>('');
    const [candidates, setCandidates] = useState<BulkImportCandidate[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [importing, setImporting] = useState(false);
    const [lastImport, setLastImport] = useState<BulkImportResponse | null>(null);
    const [toast, setToast] = useState<string | null>(null);

    const filters: EasyFlexWorkerFilters = useMemo(
        () => ({
            page,
            limit,
            search: search || undefined,
            profession: profession || undefined,
            hasActivePlacement: hasActivePlacement === '' ? undefined : hasActivePlacement === 'yes',
        }),
        [page, limit, search, profession, hasActivePlacement],
    );

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const res = await fetchBulkImportCandidates(filters);
            setCandidates(res.data || []);
            setTotal(res.pagination?.total || 0);
        } catch (err: any) {
            setLoadError(err?.message ?? 'Failed to load candidates');
            setCandidates([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        load();
    }, [load]);

    const importableRegNums = useMemo(
        () =>
            candidates
                .filter((c) => !c.alreadyImported && typeof c.easyflexRegistrationNumber === 'number')
                .map((c) => c.easyflexRegistrationNumber as number),
        [candidates],
    );

    const allOnPageSelected =
        importableRegNums.length > 0 && importableRegNums.every((n) => selected.has(n));
    const someOnPageSelected = importableRegNums.some((n) => selected.has(n)) && !allOnPageSelected;

    const togglePage = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allOnPageSelected) {
                for (const n of importableRegNums) next.delete(n);
            } else {
                for (const n of importableRegNums) next.add(n);
            }
            return next;
        });
    };

    const toggleOne = (regNum: number) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(regNum)) next.delete(regNum);
            else next.add(regNum);
            return next;
        });
    };

    const handleImport = async () => {
        const selectedWorkers: EasyFlexWorker[] = candidates.filter(
            (c) => selected.has(c.easyflexRegistrationNumber as number) && !c.alreadyImported,
        );
        if (selectedWorkers.length === 0) return;
        setImporting(true);
        try {
            const res = await bulkImportEasyflexWorkers(selectedWorkers);
            setLastImport(res);
            const { created, alreadyImported, errors } = res.summary;
            setToast(
                `Import done — ${created} created, ${alreadyImported} already imported, ${errors} errors`,
            );
            setSelected(new Set());
            await load();
        } catch (err: any) {
            setToast(err?.message ?? 'Bulk import failed');
        } finally {
            setImporting(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h5">Bulk import EasyFlex workers</Typography>
                <Button component={RouterLink} to="/resources/workers/pending-sessions" variant="outlined">
                    Pending sessions →
                </Button>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                    <TextField
                        label="Search name / email / phone"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                setPage(0);
                                setSearch(searchInput.trim());
                            }
                        }}
                        size="small"
                        sx={{ minWidth: 280 }}
                    />
                    <TextField
                        label="Profession key"
                        value={profession}
                        onChange={(e) => {
                            setPage(0);
                            setProfession(e.target.value);
                        }}
                        size="small"
                        sx={{ minWidth: 200 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Active placement</InputLabel>
                        <Select
                            value={hasActivePlacement}
                            label="Active placement"
                            onChange={(e) => {
                                setPage(0);
                                setHasActivePlacement(e.target.value);
                            }}
                        >
                            <MenuItem value="">Any</MenuItem>
                            <MenuItem value="yes">Yes</MenuItem>
                            <MenuItem value="no">No</MenuItem>
                        </Select>
                    </FormControl>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        variant="contained"
                        onClick={handleImport}
                        disabled={importing || selected.size === 0}
                    >
                        {importing ? <CircularProgress size={20} /> : `Import ${selected.size} workers`}
                    </Button>
                </Stack>
            </Paper>

            {loadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {loadError}
                </Alert>
            )}
            {lastImport && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Last import — created {lastImport.summary.created}, already imported{' '}
                    {lastImport.summary.alreadyImported}, errors {lastImport.summary.errors}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={allOnPageSelected}
                                    indeterminate={someOnPageSelected}
                                    onChange={togglePage}
                                    disabled={importableRegNums.length === 0}
                                />
                            </TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>EasyFlex reg #</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Occupation</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <CircularProgress size={20} />
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && candidates.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        No candidates matched the current filters.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading &&
                            candidates.map((c) => {
                                const regNum = c.easyflexRegistrationNumber;
                                const isSelected = typeof regNum === 'number' && selected.has(regNum);
                                return (
                                    <TableRow key={c.id} hover selected={isSelected}>
                                        <TableCell padding="checkbox">
                                            <Tooltip
                                                title={
                                                    c.alreadyImported
                                                        ? 'Already imported'
                                                        : typeof regNum !== 'number'
                                                            ? 'Missing EasyFlex registration #'
                                                            : ''
                                                }
                                            >
                                                <span>
                                                    <Checkbox
                                                        checked={isSelected}
                                                        disabled={c.alreadyImported || typeof regNum !== 'number'}
                                                        onChange={() => regNum != null && toggleOne(regNum)}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            {c.firstName} {c.lastName}
                                        </TableCell>
                                        <TableCell>{regNum ?? '—'}</TableCell>
                                        <TableCell>{c.email}</TableCell>
                                        <TableCell>{c.phone}</TableCell>
                                        <TableCell>
                                            {c.currentOccupationTranslated?.optimaProfessionName ||
                                                c.currentOccupation ||
                                                '—'}
                                        </TableCell>
                                        <TableCell>
                                            {c.alreadyImported ? (
                                                <Chip
                                                    label="Already imported"
                                                    size="small"
                                                    color="success"
                                                    component={c.importedWorkerId ? RouterLink : 'span'}
                                                    to={
                                                        c.importedWorkerId
                                                            ? `/workers/${c.importedWorkerId}`
                                                            : undefined
                                                    }
                                                    clickable={!!c.importedWorkerId}
                                                />
                                            ) : (
                                                <Chip label={c.status} size="small" />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={limit}
                    onRowsPerPageChange={(e) => {
                        setLimit(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </TableContainer>

            <Snackbar
                open={!!toast}
                autoHideDuration={6000}
                onClose={() => setToast(null)}
                message={toast ?? ''}
            />
        </Box>
    );
}
